import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { createClient } from '../schemas/generated/client/client.gen';
import { PeanudsApi } from '../schemas/generated/sdk.gen';
import { reportAuthFlowBlocked } from '../observability/runtime-observability';
import {
  handleMfaRequired,
  handlePendingLivenessCheck,
  type PendingLivenessLaunchResult,
} from './http-security-handlers';

export const PENDING_LIVENESS_CHECK_STATUS = 409;
export const PENDING_LIVENESS_CHECK_ERROR_CODE = 'GENERIC.PENDING_LIVENESS_CHECK';
export const MFA_REQUIRED_STATUS = 403;
export const MFA_REQUIRED_ERROR_CODE = 'GENERIC.MFA_REQUIRED';
export const OAUTH_MFA_REQUIRED_ERROR = 'mfa_required';

type ApiErrorResponseLike = {
  status: number;
  data: unknown;
};

type PendingLivenessCheckResponseData = {
  error?:
    | {
        code?: string;
        message?: string;
      }
    | string
    | null;
  error_description?: string;
};

type MfaRequiredResponseData = PendingLivenessCheckResponseData;

type ApiAuthSession = {
  jwtToken: string | null;
  idToken: string | null;
  isTokenExpired: boolean;
  setLastActiveTime: () => void;
  setSessionActiveUntil: (sessionActiveUntil: number) => void;
};

type ApiAuthHandlers = {
  getAuthSession: () => ApiAuthSession;
  redirectToLogin: () => Promise<void> | void;
};

const SESSION_ACTIVE_UNTIL_HEADER = 'x-session-active-until';
export const SESSION_ACTIVE_UNTIL_FALLBACK_MS = 5 * 60 * 1000;

let apiAuthHandlers: ApiAuthHandlers | null = null;

export const registerApiAuthHandlers = (handlers: ApiAuthHandlers) => {
  apiAuthHandlers = handlers;
};

export const resetApiAuthHandlers = () => {
  apiAuthHandlers = null;
};

export const getApiAuthHandlers = () => {
  if (apiAuthHandlers === null) {
    throw new AuthenticatedRequestError('API auth handlers are not registered.');
  }

  return apiAuthHandlers;
};

export class AuthenticatedRequestError extends Error {
  constructor(message = 'Unable to establish an authenticated session for this request.') {
    super(message);
    this.name = 'AuthenticatedRequestError';
  }
}

export class PendingLivenessCheckError extends Error {
  readonly code = PENDING_LIVENESS_CHECK_ERROR_CODE;
  readonly status = PENDING_LIVENESS_CHECK_STATUS;
  readonly suppressGenericToast = true;

  constructor(
    readonly response: ApiErrorResponseLike & { data: PendingLivenessCheckResponseData },
    readonly launchResult: Promise<PendingLivenessLaunchResult>,
    readonly originalError?: unknown,
  ) {
    super(
      getErrorMessage(response.data) ??
        'A liveness check is required before this action can continue.',
    );
    this.name = 'PendingLivenessCheckError';
  }
}

export class MfaRequiredError extends Error {
  readonly code: string;
  readonly status: number;
  readonly suppressGenericToast = true;

  constructor(
    readonly response: ApiErrorResponseLike & { data: MfaRequiredResponseData },
    readonly redirectPromise: Promise<void>,
    readonly originalError?: unknown,
  ) {
    super(
      getErrorMessage(response.data) ??
        'MFA verification is required before this action can continue.',
    );
    this.name = 'MfaRequiredError';
    this.code = getMfaRequiredCode(response.data);
    this.status = response.status;
  }
}

const getErrorMessages = (data: unknown) => {
  if (typeof data !== 'object' || data === null) {
    return [] as string[];
  }

  const { error, error_description: errorDescription } = data as {
    error?: { message?: unknown } | unknown;
    error_description?: unknown;
  };

  const messages: string[] = [];

  if (typeof error !== 'object' || error === null) {
    if (typeof errorDescription === 'string' && errorDescription.length > 0) {
      messages.push(errorDescription);
    }

    if (typeof error === 'string' && error.length > 0) {
      messages.push(error);
    }

    return [...new Set(messages)];
  }

  const message = (error as { message?: unknown }).message;

  if (typeof message === 'string' && message.length > 0) {
    messages.push(message);
  }

  if (typeof errorDescription === 'string' && errorDescription.length > 0) {
    messages.push(errorDescription);
  }

  return [...new Set(messages)];
};

const getErrorMessage = (data: unknown) => getErrorMessages(data)[0] ?? null;

const getErrorCode = (data: unknown) => {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const { error } = data as {
    error?: { code?: unknown } | unknown;
  };

  if (typeof error !== 'object' || error === null) {
    return typeof error === 'string' && error.length > 0 ? error : null;
  }

  const code = (error as { code?: unknown }).code;

  return typeof code === 'string' && code.length > 0 ? code : null;
};

const normalizeErrorMessage = (message: string) => message.trim().toLowerCase().replace(/_/g, ' ');

const hasPendingLivenessMessage = (data: unknown): data is PendingLivenessCheckResponseData => {
  return getErrorMessages(data).some((message) =>
    normalizeErrorMessage(message).includes('liveness'),
  );
};

const hasMfaRequiredMessage = (data: unknown): data is MfaRequiredResponseData => {
  return getErrorMessages(data).some((message) => {
    const normalizedMessage = normalizeErrorMessage(message);

    return normalizedMessage.includes('mfa') || normalizedMessage.includes('multifactor');
  });
};

const getMfaRequiredCode = (data: MfaRequiredResponseData) =>
  getErrorCode(data) ?? MFA_REQUIRED_ERROR_CODE;

export const isPendingLivenessCheckResponse = (
  response?: ApiErrorResponseLike | null,
): response is ApiErrorResponseLike & { data: PendingLivenessCheckResponseData } => {
  return hasPendingLivenessMessage(response?.data);
};

export const isMfaRequiredResponse = (
  response?: ApiErrorResponseLike | null,
): response is ApiErrorResponseLike & { data: MfaRequiredResponseData } => {
  return hasMfaRequiredMessage(response?.data);
};

export const isPendingLivenessCheckSentinelError = (
  error: unknown,
): error is PendingLivenessCheckError => error instanceof PendingLivenessCheckError;

export const isMfaRequiredSentinelError = (error: unknown): error is MfaRequiredError =>
  error instanceof MfaRequiredError;

const TOKEN_EXPIRED_ERROR_CODE = 'TOKEN_EXPIRED';

let loginRedirectPromise: Promise<void> | null = null;
const triggerLoginRedirect = () => {
  if (!loginRedirectPromise) {
    loginRedirectPromise = Promise.resolve()
      .then(() => getApiAuthHandlers().redirectToLogin())
      .catch(() => undefined)
      .finally(() => {
        loginRedirectPromise = null;
      });
  }

  return loginRedirectPromise;
};

const isTokenExpiredResponse = (response?: ApiErrorResponseLike | null) => {
  return getErrorCode(response?.data) === TOKEN_EXPIRED_ERROR_CODE;
};

const getResponseHeaderValue = (headers: unknown, headerName: string): string | null => {
  if (!headers || typeof headers !== 'object') {
    return null;
  }

  if ('get' in headers && typeof headers.get === 'function') {
    const value = headers.get(headerName);

    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  const matchingEntry = Object.entries(headers).find(([key]) => {
    return key.toLowerCase() === headerName.toLowerCase();
  });

  if (!matchingEntry) {
    return null;
  }

  const [, value] = matchingEntry;

  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  return null;
};

const parseSessionActiveUntil = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsedValue = Date.parse(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const syncSessionActivityFromResponseHeaders = (headers: unknown) => {
  const sessionActiveUntil = parseSessionActiveUntil(
    getResponseHeaderValue(headers, SESSION_ACTIVE_UNTIL_HEADER),
  );

  if (sessionActiveUntil === null) {
    return;
  }

  const authSessionStore = getApiAuthHandlers().getAuthSession();

  authSessionStore.setLastActiveTime();
  authSessionStore.setSessionActiveUntil(sessionActiveUntil);
};

const defaultHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

// LEGACY AXIOS CLIENT
export const publicApiClient = axios.create({ headers: defaultHeaders });
export const authenticatedApiClient = axios.create({
  headers: defaultHeaders,
});

// Attach authentication headers.
const attachAuthenticationHeaders = async (config: InternalAxiosRequestConfig) => {
  const authSessionStore = getApiAuthHandlers().getAuthSession();

  if (authSessionStore.jwtToken && authSessionStore.idToken && !authSessionStore.isTokenExpired) {
    config.headers.Authorization = `Bearer ${authSessionStore.jwtToken}`;
    config.headers['X-Id-Token'] = `Bearer ${authSessionStore.idToken}`;

    return config;
  }

  reportAuthFlowBlocked({
    operation: 'legacy-authenticated-request',
    failureKind: 'missing-valid-token',
    tags: {
      hasJwtToken: Boolean(authSessionStore.jwtToken),
      hasIdToken: Boolean(authSessionStore.idToken),
      isTokenExpired: authSessionStore.isTokenExpired,
    },
  });
  void triggerLoginRedirect();
  throw new AuthenticatedRequestError();
};

authenticatedApiClient.interceptors.request.use(attachAuthenticationHeaders);

const installAuthenticatedResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      syncSessionActivityFromResponseHeaders(response.headers);

      return response;
    },
    async (error: AxiosError) => {
      if (error.response) {
        syncSessionActivityFromResponseHeaders(error.response.headers);
      }

      if (isPendingLivenessCheckResponse(error.response)) {
        const launchResult = handlePendingLivenessCheck();
        return Promise.reject(new PendingLivenessCheckError(error.response, launchResult, error));
      }

      if (isMfaRequiredResponse(error.response)) {
        const redirectPromise = handleMfaRequired();

        console.log(redirectPromise);
        return Promise.reject(new MfaRequiredError(error.response, redirectPromise, error));
      }

      const isUnauthorizedResponse = error.response?.status === 401;
      const isExpiredTokenResponse = isTokenExpiredResponse(error.response);

      if (!isUnauthorizedResponse && !isExpiredTokenResponse) {
        return Promise.reject(error);
      }

      reportAuthFlowBlocked({
        operation: 'authenticated-response',
        failureKind: isExpiredTokenResponse ? 'token-expired-response' : 'unauthorized-response',
        tags: {
          backendStatus: error.response?.status ?? null,
        },
      });
      void triggerLoginRedirect();
      return Promise.reject(error);
    },
  );
};

installAuthenticatedResponseInterceptor(authenticatedApiClient);

// HEY-API SDK (with Zod validation)
const strictJsonParsing = { ...axios.defaults.transitional, silentJSONParsing: false };
const publicClient = createClient({ headers: defaultHeaders, transitional: strictJsonParsing });
const authenticatedClient = createClient({
  headers: defaultHeaders,
  transitional: strictJsonParsing,
});

export const configureApiBaseUrl = (baseURL: string | undefined) => {
  publicApiClient.defaults.baseURL = baseURL;
  authenticatedApiClient.defaults.baseURL = baseURL;
  publicClient.setConfig({ baseURL });
  authenticatedClient.setConfig({ baseURL });
};

authenticatedClient.instance.interceptors.request.use(attachAuthenticationHeaders);
installAuthenticatedResponseInterceptor(authenticatedClient.instance);

export const publicPeanudsApi = new PeanudsApi({ client: publicClient });
export const peanudsApi = new PeanudsApi({ client: authenticatedClient });
